import React, { useCallback, useEffect, useRef, useState } from 'react';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { tr } from '../config/strings';
import { PlaybookLang } from '../config/types';
import styles from './PlaybookFab.module.scss';

const FAB_Z_INDEX = 900;
const FAB_SIZE = 40;
const EDGE_GAP = 8;
const DRAG_THRESHOLD = 4;
const INTRO_MS = 5000;
const STORAGE_PREFIX = 'host_playbook_fab_';
const INTRO_KEY = `${STORAGE_PREFIX}intro_seen`;

interface FabPosition {
  /** Edge the button is anchored to, so its label always unfolds towards the middle of the screen. */
  side: 'left' | 'right';
  /** Distance from that edge. */
  x: number;
  /** Distance from the bottom edge. */
  bottom: number;
}

const DEFAULT_POSITION: FabPosition = { side: 'left', x: 16, bottom: 16 };

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), Math.max(min, max));

const clampPosition = (pos: FabPosition): FabPosition => ({
  ...pos,
  x: clamp(pos.x, EDGE_GAP, window.innerWidth - FAB_SIZE - EDGE_GAP),
  bottom: clamp(pos.bottom, EDGE_GAP, window.innerHeight - FAB_SIZE - EDGE_GAP),
});

/**
 * Where the user dragged the button during this page view. Kept in memory only, so the panel opening and closing
 * (which unmounts the button) does not move it, while a page reload puts it back at the default corner.
 */
let sessionPosition: FabPosition | null = null;

interface Props {
  lang: PlaybookLang;
  isMobile: boolean;
  onClick: () => void;
}

/**
 * Floating guide button. Rests as a small icon, unfolds its label on hover / focus (and once, for a few seconds,
 * the first time it is shown), and can be dragged anywhere so it never has to cover data on screen.
 */
const PlaybookFab = ({ lang, isMobile, onClick }: Props) => {
  const { token } = theme.useToken();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dragRef = useRef<{ startX: number; startY: number; rect: DOMRect; moved: boolean } | null>(null);
  const justDraggedRef = useRef(false);

  const [position, setPosition] = useState<FabPosition>(() => sessionPosition ?? DEFAULT_POSITION);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [intro, setIntro] = useState(false);
  const [dragging, setDragging] = useState(false);

  // First time on this browser: show the full label for a few seconds, then fold it away for good.
  useEffect(() => {
    if (isMobile) return;
    let seen = false;
    try {
      seen = window.localStorage.getItem(INTRO_KEY) === '1';
    } catch {
      seen = true;
    }
    if (seen) return;
    setIntro(true);
    const timer = window.setTimeout(() => {
      setIntro(false);
      try {
        window.localStorage.setItem(INTRO_KEY, '1');
      } catch {
        // ignore
      }
    }, INTRO_MS);
    return () => window.clearTimeout(timer);
  }, [isMobile]);

  // Keep the button on screen when the window shrinks.
  useEffect(() => {
    const onResize = () => setPosition((pos) => clampPosition(pos));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0 || !buttonRef.current) return;
    buttonRef.current.setPointerCapture(e.pointerId);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      rect: buttonRef.current.getBoundingClientRect(),
      moved: false,
    };
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (!drag.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
    if (!drag.moved) {
      drag.moved = true;
      setDragging(true);
    }
    // While dragging the button follows the pointer by its left edge, using the folded size.
    setPosition(
      clampPosition({
        side: 'left',
        x: drag.rect.left + dx,
        bottom: window.innerHeight - (drag.rect.top + dy + FAB_SIZE),
      }),
    );
  }, []);

  const onPointerUp = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    dragRef.current = null;
    buttonRef.current?.releasePointerCapture(e.pointerId);
    if (!drag?.moved) return;

    justDraggedRef.current = true; // swallow the click that follows a drag
    window.setTimeout(() => {
      justDraggedRef.current = false;
    }, 0);
    setDragging(false);

    // Anchor to the nearer side so the label always has room to unfold.
    setPosition((pos) => {
      const toRight = pos.x + FAB_SIZE / 2 > window.innerWidth / 2;
      const next: FabPosition = toRight ? { ...pos, side: 'right', x: window.innerWidth - pos.x - FAB_SIZE } : pos;
      const clamped = clampPosition(next);
      sessionPosition = clamped;
      return clamped;
    });
  }, []);

  const expanded = !isMobile && !dragging && (hovered || focused || intro);
  const edge = position.side === 'left' ? { left: position.x } : { right: position.x };

  return (
    <button
      ref={buttonRef}
      type='button'
      aria-label={tr('fab', lang)}
      title={tr('fab', lang)}
      className={[
        styles.fab,
        position.side === 'left' ? styles.left : styles.right,
        expanded ? styles.expanded : '',
        dragging ? styles.dragging : '',
      ].join(' ')}
      style={{ ...edge, bottom: position.bottom, zIndex: FAB_Z_INDEX, background: token.colorPrimary }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      // Only keyboard focus unfolds the label; a mouse click or drag leaves the button folded
      onFocus={(e) => setFocused(e.currentTarget.matches(':focus-visible'))}
      onBlur={() => setFocused(false)}
      onClick={() => {
        if (!justDraggedRef.current) onClick();
      }}
    >
      <span className={styles.icon}>
        <QuestionCircleOutlined />
      </span>
      {!isMobile && <span className={styles.label}>{tr('fab', lang)}</span>}
    </button>
  );
};

export default PlaybookFab;
